<template>
  <div class="users-hours text-dark">
    <div class="users-hours__header">
      <div class="users-hours__header__left">
        <h1 class="marg-20-right">{{ $t('vue2.assign_users_hours.assign_hours') }}</h1>
        <ChangeSchoolYear :whiteTheme="true"></ChangeSchoolYear>
        <p class="marg-10-left" v-if="!isInitalLoad">{{ $t('vue2.task_overview.showing_scheduled_hours_from') }} {{
            startDate | yearFormat
          }} - {{
            endDate | yearFormat
          }}</p>
      </div>
      <div class="user-tasks__header__right">
        <p class="d-link" @click="resetDates" v-if="!isDatesPristine">{{ $t('vue2.task_overview.reset') }}</p>
        <DatePickerRangeShortcuts
          v-if="activeSchoolYear"
          :startDate="activeSchoolYear.employee_time_start"
          :endDate="activeSchoolYear.employee_time_end"
          :selectedStartDate="startDate"
          :selectedEndDate="endDate"
          class="marg-1-right"
          @shortcutSelected="setDatesFromRange">
        </DatePickerRangeShortcuts>
        <DatePickerRange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate($event, true)">
        </DatePickerRange>
        <button v-if="isShowPrintButton" class="marg-1-left" @click="print">
          <span class="icon-printer3"></span>
        </button>
      </div>
    </div>
    <div class="users border">
      <div class="users--loading" v-if="isLoadingUpdatedNumbers && !isInitalLoad && !this.numberOfWorkDaysInPeriod">
        <Spinner bg-color="#fff"></Spinner>
      </div>
      <div class="users-header users-header-second">
        <div class="flex-left">
          <div class="users-user__meta">
            <div>
              <input type="text" class="users-header__searchfield"
                     @input="debounceSearch"
                     :placeholder="$t('vue2.assign_users_hours.search_a_name')">
            </div>
            <DoFiltersFilter
              class="marg-1-left"
              :tags="tags"
              :selectedTags="appliedFilters"
              @select="toggleTagFilter"
              @clear="clearTagFilters">
            </DoFiltersFilter>
          </div>
        </div>
        <div class="flex-right users-header-second__buttons">
          <PrintTaskOverviews
            v-if="isShowPrintTaskOverviewModal"
            :tags="tags"
            :theUserCanManageAssignedHours="theUserCanManageAssignedHours"
            :schoolThresholdToHideTaskHours="schoolThresholdToHideTaskHours"
            @close="isShowPrintTaskOverviewModal = false">
          </PrintTaskOverviews>
          <SaveTaskOverview
            v-if="isShowSaveVersionTaskOverviewModal"
            @close="isShowSaveVersionTaskOverviewModal = false">
          </SaveTaskOverview>
          <DeleteTaskOverviews
            v-if="isShowDeleteTaskOverviewsModal"
            :tags="tags"
            @close="isShowDeleteTaskOverviewsModal = false">
          </DeleteTaskOverviews>
          <AssignHoursNorm
            v-if="isSchoolDanish && theUserCanManageAssignedHours"
            :prepFactor="schoolPrepFactor"
            :isLocalPoolActivated="isLocalPoolActivated"
            :isPoolsDeactivated="isPoolsDeactivated"
            :isKindergarten="false"
            :tags="tags"
            @updateUsersHours="getUsersHours">
          </AssignHoursNorm>
          <DDropdown :isShowDots="false" :isBottomStart="false" :propOffset="[5, 5]">
            <button class="do-bttn" slot="title"><span class="icon-more2"></span></button>
            <ul slot="list">
              <li @click="setIsDeactivatePoolsModalVisible(true)"
                  v-if="isSchoolDanish && theUserCanManageAssignedHours">
                {{ isPoolsDeactivated ? 'Aktiver puljer' : 'Deaktiver puljer' }}
              </li>
              <li @click="toggleShowRestColumn"
                  v-if="!isPoolsDeactivated && isSchoolDanish && theUserCanManageAssignedHours">
                {{ isShowRestColumn ? 'Skjul rest' : 'Vis rest' }}
              </li>
              <li @click="setIsChangePrepFactorModal(true)" v-if="isSchoolDanish && theUserCanManageAssignedHours">
                {{ !schoolPrepFactor ? 'Aktiver forbereldesesfaktor' : 'Forberedelsesfaktor ' + schoolPrepFactor }}
              </li>
              <li @click="setIsModalPedagogicalSetting(true)" v-if="isSchoolDanish && theUserCanManageAssignedHours">
                Grupper med pædagogoptælling
              </li>
              <li v-if="isSchoolDanish && theUserCanManageAssignedHours"
                  @click="isShowTaskOverviewSettings = true">
                Design opgaveoversigter
              </li>
              <li v-if="isSchoolDanish && theUserCanManageAssignedHours"
                  @click="isShowSaveVersionTaskOverviewModal = true">
                {{ $t('vue2.task_versions.save_new_version_task_overviews') }}
              </li>
              <li v-if="isSchoolDanish && theUserCanManageAssignedHours"
                  @click="isShowPrintTaskOverviewModal = true">
                {{ $t('vue2.task_versions.print_latest_version_task_overviews') }}
              </li>
              <li v-if="isSchoolDanish && theUserCanManageAssignedHours"
                  @click="isShowDeleteTaskOverviewsModal = true">
                Håndter versioner af opgaveoversigter
              </li>
              <li @click="asyncLoadXLSXAndDownload">
                {{ i18n['download_to_excel'] }}
              </li>
              <li @click="isShowEmploymentRatesModal = true" v-if="isSchoolDanish && theUserCanManageAssignedHours">
                Vis ansættelsesgrader
              </li>
              <li @click="isShowAvgTeachingHoursModal = true" v-if="isSchoolDanish && theUserCanManageAssignedHours">
                Gennemsnits undervisningstal
              </li>
              <li @click="isShowTransferRestModal = true"
                  v-if="isPoolsDeactivated && isSchoolDanish && theUserCanManageAssignedHours">
                Overfør positiv rest til brik
              </li>
              <li @click="setIsChangeViewModal" v-if="isSchoolFromCO">{{ i18n['change_view'] }}</li>
              <li @click="updateUserHours">
                {{ $t('vue2.assign_users_hours.update') }}
              </li>
              <li @click="setIsShowEmploymentEnded(true)" v-if="isSchoolDanish">
                Brudt ansættelse
              </li>
              <li @click="isShowWorkedHoursStatement = true" v-if="isSchoolSystem && isSchoolDanish && theUserCanManageAssignedHours">
                Opgørelse af ansættelse
              </li>
            </ul>
          </DDropdown>
        </div>
      </div>
      <div v-if="isInitalLoad || isLoadingSubjectsOrTasks" style="margin: 100px auto">
        <Spinner bgColor="#fff"></Spinner>
      </div>
      <AllUsers
        v-else
        :theUserCanManageAssignedHours="theUserCanManageAssignedHours"
        :prepFactor="schoolPrepFactor"
        :isPrepFactorForSubstitutePools="isPrepFactorForSubstitutePools"
        :viewSettings="viewSettings"
        :schoolid="schoolId"
        :users="filteredUsers"
        :isDatesPristine="isDatesPristine"
        :numberOfWorkDays="numberOfWorkDays"
        :numberOfWorkDaysInPeriod="numberOfWorkDaysInPeriod"
        :isLocalPoolActivated="isLocalPoolActivated"
        :locale="locale"
        :isPoolsDeactivated="isPoolsDeactivated"
        :isShowRestColumn="isShowRestColumn"
        @setActiveUser="setActiveUser"
        @setTheUserToShowTasks="setTheUserToShowTasks"
        @updatedUser="updatedUser">
      </AllUsers>
    </div>
    <DModal v-if="isChangeViewModal" @close="setIsChangeViewModal" :isBgOverlay="true" :show-close="false">
      <div class="modal-assign-hours-view" slot="insertComponent">
        <div class="do-modal__header">
          <h2>{{ i18n['change_view'] }}</h2>
          <div class="icon__bg-circle" @click="setIsChangeViewModal">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="do-modal__body">
          <ul>
            <li v-for="(setting, key) in viewSettingsI18N"
                :key="key"
                class="modal-assign-hours-view__item"
                @click.stop="setViewSetting(key, setting.active)">
              <label class="d-label">{{ setting.title }}</label>
              <div class="do-switch" :class="{'do-switch--active': setting.active}"></div>
            </li>
          </ul>
        </div>
        <div class="do-modal__footer">
          <button class="do-bttn" @click="setIsChangeViewModal">{{ $t('vue2.common.done') }}</button>
        </div>
      </div>
    </DModal>
    <ShowUserAbsence
      v-if="activeUserId"
      @close="activeUserId = null"
      :eventsFilter="activeUserEventFilter"
      :userId="activeUserId">
    </ShowUserAbsence>
    <DModal v-if="theUserToShowTasks" @close="closeTheUserToShowTasks" :isBgOverlay="true" :show-close="false" :isPrintable="true">
      <div class="user-task__container" slot="insertComponent">
        <div class="user-task__container__header no-print">
          <h2></h2>
          <div class="icon__bg-circle ww-modal__header__close" @click="closeTheUserToShowTasks">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <UserOverviewTabs
          v-if="theUserToShowTasks"
          :user="theUserToShowTasks"
          initialView="hours"
          :initialStartDate="startDate"
          :initialEndDate="endDate"
        ></UserOverviewTabs>
      </div>
    </DModal>
    <ModalPedagogicalSetting
      v-if="isModalPedagogicalSetting"
      :tags="tags"
      :schoolId="schoolId"
      :preSelectedTags="userTagsToShowSum"
      :preSelectedTagsSumWorkHours="userTagsForSumWorkHours"
      @saved="getUsersHours"
      @close="setIsModalPedagogicalSetting(false)">
    </ModalPedagogicalSetting>
    <DModal v-if="isChangePrepFactorModal" @close="setIsChangePrepFactorModal(false)" :isBgOverlay="true"
            :show-close="false">
      <div class="do-modal" slot="insertComponent">
        <div class="do-modal__header">
          <h2>Forberedelsesfaktor</h2>
          <div class="icon__bg-circle ww-modal__header__close" @click="setIsChangePrepFactorModal(false)">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="do-modal__body">
          <div class="d-explainer-box text-explainer text-explainer__error">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="tw-icon icon-help">
              <path class="primary" d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20z"/>
              <path class="secondary"
                    d="M12 19.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm1-5.5a1 1 0 0 1-2 0v-1.41a1 1 0 0 1 .55-.9L14 10.5C14.64 10.08 15 9.53 15 9c0-1.03-1.3-2-3-2-1.35 0-2.49.62-2.87 1.43a1 1 0 0 1-1.8-.86C8.05 6.01 9.92 5 12 5c2.7 0 5 1.72 5 4 0 1.3-.76 2.46-2.05 3.24L13 13.2V14z"/>
            </svg>
            Aktivering af forberedelsesfaktor gør, at timer til forberedelse automatisk opdateres, når undervisning
            tildeles til en underviser.
          </div>
          <div class="d-label-and-toggle" @click.stop="setIsThePrepFactorActive">
            <label class="d-label color-dark">Aktiver forberedelsesfaktor</label>
            <div class="do-switch" :class="{'do-switch--active': isThePrepFactorActive}"></div>
          </div>
          <div v-if="isThePrepFactorActive" class="prep-factor-input marg-1-bottom">
            <label class="d-label">Indtast jeres forberedelsesfaktor</label>
            <input type="number" v-model="thePrepFactor" @keyup.enter="savePrepFactor" ref="the_prep_factor_input"/>
          </div>
          <div v-if="isThePrepFactorActive" class="d-label-and-toggle"
               @click.stop="isThePrepFactorForSubstitutePools = !isThePrepFactorForSubstitutePools">
            <label class="d-label color-dark">Medregn forberedelsesfaktor for vikarpuljer</label>
            <div class="do-switch" :class="{'do-switch--active': isThePrepFactorForSubstitutePools}"></div>
          </div>
        </div>
        <div class="do-modal__footer">
          <button class="do-bttn marg-1-right" @click="setIsChangePrepFactorModal(false)">Annuller</button>
          <button class="do-bttn do-bttn--submit" :class="{'do-bttn--submit--loading': isSavingPrepFactor}"
                  @click="savePrepFactor">{{ $t('vue2.common.save') }}
          </button>
        </div>
      </div>
    </DModal>
    <DModal v-if="isShowEmploymentEnded" @close="setIsShowEmploymentEnded(false)" :isBgOverlay="true"
            :show-close="false">
      <div class="do-modal" slot="insertComponent">
        <div class="do-modal__header">
          <h2>Brudt ansættelse</h2>
          <div class="icon__bg-circle ww-modal__header__close" @click="setIsShowEmploymentEnded(false)">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="do-modal__body">
          <AssignHoursEmploymentEnded
            :schoolId="schoolId"
            :users="usersForList">
          </AssignHoursEmploymentEnded>
        </div>
      </div>
    </DModal>
    <DModal v-if="isShowWorkedHoursStatement" @close="isShowWorkedHoursStatement = false" :isBgOverlay="true"
            :show-close="false">
      <div class="do-modal do-modal--fullscreen" slot="insertComponent">
        <div class="do-modal__header">
          <h2>{{ workedHoursStatementTitle }}</h2>
          <div class="icon__bg-circle ww-modal__header__close" @click="isShowWorkedHoursStatement = false">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="do-modal__body">
          <AssignHoursWorkedHoursStatement
            :schoolId="schoolId"
            :users="usersForList"
            @title="workedHoursStatementTitle = $event">
          </AssignHoursWorkedHoursStatement>
        </div>
      </div>
    </DModal>
    <TaskOverviewSettings
      v-if="isShowTaskOverviewSettings"
      @close="isShowTaskOverviewSettings = false">
    </TaskOverviewSettings>
    <AssignHoursDeactivatePoolsModal
      v-if="isDeactivatePoolsModalVisible"
      :isPoolsDeactivated="isPoolsDeactivated"
      @reReadNumbers="reReadNumbers"
      @close="setIsDeactivatePoolsModalVisible(false)">
    </AssignHoursDeactivatePoolsModal>
    <AssignHoursEmploymentRates
      v-if="isShowEmploymentRatesModal"
      @close="isShowEmploymentRatesModal = false">
    </AssignHoursEmploymentRates>
    <AvgTeachingHoursModal
      v-if="isShowAvgTeachingHoursModal"
      :schoolId="schoolId"
      :schoolYearId="activeSchoolYear.id"
      @close="isShowAvgTeachingHoursModal = false">
    </AvgTeachingHoursModal>
    <AssignHoursTransferRestModal
      v-if="isShowTransferRestModal"
      :users="usersWithPositiveRest"
      :schoolId="schoolId"
      :schoolYearId="activeSchoolYear.id"
      :isLocalPoolActivated="isLocalPoolActivated"
      @transferred="readSubjectsAndTasks"
      @close="isShowTransferRestModal = false">
    </AssignHoursTransferRestModal>
  </div>
</template>

<script>
import tag from '../Tagging/Tag'
import AllUsers from './AssignHoursAllUsersFlat'
import Spinner from '../common/Loader'
import SpinnerButton from '../common/SpinnerButton'
import docendo from '../../docendo'
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import {mapGetters} from 'vuex'
import moment from 'moment'
import ShowUserAbsence from '../User/UserEventList'
import DModal from '../common/DModal'
import UserOverviewTabs from '../User/UserOverviewTabs'
import DatePickerRange from '../datepicker/DatePickerRange'
import AssignHoursNorm from './AssignHoursNorm'
import DatePickerRangeShortcuts from '../datepicker/DatePickerRangeShortcuts'
import eventbus from '../../eventbus'
import SaveTaskOverview from '../TaskOverview/SaveTaskOverview'
import DeleteTaskOverviews from '../TaskOverview/DeleteTaskOverviews'
import PrintTaskOverviews from '../Print/PrintTaskOverviews'
import DDropdown from '../common/DDropdown'
import AssignHoursEmploymentEnded from './AssignHoursEmploymentEnded.vue'
import AssignHoursWorkedHoursStatement from './AssignHoursWorkedHoursStatement.vue'
import TaskOverviewSettings from '../TaskOverviewSettings/TaskOverviewSettings.vue'
import AssignHoursDeactivatePoolsModal from './AssignHoursDeactivatePoolsModal.vue'
import AssignHoursEmploymentRates from './AssignHoursEmploymentRates.vue'
import AvgTeachingHoursModal from './AvgTeachingHoursModal.vue'
import AssignHoursTransferRestModal from './AssignHoursTransferRestModal.vue'
import ModalPedagogicalSetting from './ModalPedagogicalSetting.vue'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import {parseNumericPreparationFactor} from '../../utils/preparationFactor'

let XLSX = null

const I18N = {
  da: {
    name: 'Navn',
    workTime: 'Arbejdstid',
    assigned: 'Tildelt',
    scheduled: 'Skemalagt',
    difference: 'Forskel',
    teaching: 'Undervisning',
    otherTasks: 'Øvrige Opgaver',
    preparation: 'Forberedelse',
    absence: 'Fravær',
    substitute: 'Vikartimer',
    sfo: 'SFO',
    download: 'Download',
    download_to_excel: 'Download til Excel',
    updating_numbesr_you_will_be_notified: 'Opdaterer alle tal, du får en notifikation, når alle tal er opdateret.',
    show_assigned_title: 'Vis tildelt og forskel',
    show_in_minutes_title: 'Vis skemalagt i minutter',
    change_view: 'Ændre visningen',
    employee_id: 'Medarbejder-ID',
    pool: 'Lokalpulje'
  },
  en: {
    name: 'Name',
    workTime: 'Work Time',
    assigned: 'Assigned',
    scheduled: 'Scheduled',
    difference: 'Difference',
    teaching: 'Teaching',
    otherTasks: 'Other Tasks',
    preparation: 'Preparation',
    absence: 'Absence',
    substitute: 'Substitute',
    sfo: 'SFO',
    download: 'Download',
    download_to_excel: 'Download to Excel',
    updating_numbers_you_will_be_notified: 'Updating all numbers, you will be notified when everything is updated.',
    show_assigned_title: 'Show assigned and difference',
    show_in_minutes_title: 'Show actual hours in minutes',
    change_view: 'Change view',
    employee_id: 'Employee id',
    pool: 'Pool'
  },
  nb: {
    name: 'Navn',
    workTime: 'Arbeidstid',
    assigned: 'Tildelt',
    scheduled: 'Planlagt',
    difference: 'Forskjell',
    teaching: 'Undervisning',
    otherTasks: 'Andre oppgaver',
    preparation: 'Forberedelse',
    absence: 'Fravær',
    substitute: 'Vikartimer',
    sfo: 'SFO',
    download: 'Last ned',
    download_to_excel: 'Last ned til Excel',
    updating_numbers_you_will_be_notified: 'Oppdaterer alle tall, du vil få en varsling når alle tall er oppdatert.',
    show_assigned_title: 'Vis tildelt og forskjell',
    show_in_minutes_title: 'Vis planlagt i minutter',
    change_view: 'Endre visningen',
    employee_id: 'Ansatt ID',
    pool: 'Lokalpulje'
  }
}

export default {
  components: {
    ModalPedagogicalSetting,
    AssignHoursEmploymentRates,
    AvgTeachingHoursModal,
    AssignHoursTransferRestModal,
    AssignHoursDeactivatePoolsModal,
    TaskOverviewSettings,
    AssignHoursEmploymentEnded,
    AssignHoursWorkedHoursStatement,
    DDropdown,
    PrintTaskOverviews,
    SaveTaskOverview,
    DeleteTaskOverviews,
    DatePickerRangeShortcuts,
    AssignHoursNorm,
    DatePickerRange,
    UserOverviewTabs, DModal, ShowUserAbsence, tag, Spinner, DoFiltersFilter, AllUsers, ChangeSchoolYear, SpinnerButton
  },
  data () {
    return {
      isModalPedagogicalSetting: false,
      isShowTaskOverviewSettings: false,
      isShowEmploymentRatesModal: false,
      isShowAvgTeachingHoursModal: false,
      isShowTransferRestModal: false,
      isDeactivatePoolsModalVisible: false,
      isShowEmploymentEnded: false,
      isShowWorkedHoursStatement: false,
      workedHoursStatementTitle: 'Opgørelse af ansættelse',
      isSavingPrepFactor: false,
      isThePrepFactorActive: false,
      isThePrepFactorForSubstitutePools: false,
      thePrepFactor: 0.5,
      isChangePrepFactorModal: false,
      selectedUsers: [],
      tags: [],
      createTagActive: false,
      searchText: '',
      appliedFilters: [],
      usersWithScheduledHours: [],
      usersWithAssignedHours: [],
      isInitalLoad: true,
      activeUserId: null,
      activeUserEventFilter: 'absence',
      theUserToShowTasks: null,
      startDate: null,
      endDate: null,
      isLoadingUpdatedNumbers: false,
      debounceTimer: null,
      isDownloading: false,
      allVacationsDays: [],
      isUpdateBttnLoading: false,
      isShowPrintTaskOverviewModal: false,
      isShowSaveVersionTaskOverviewModal: false,
      isShowDeleteTaskOverviewsModal: false,
      isChangeViewModal: false,
      viewSettings: {
        show_assigned: {
          title: 'Show assigned and difference',
          active: true
        },
        show_in_minutes: {
          title: 'Show actual hours in minutes',
          active: false
        }
      }
    }
  },
  filters: {
    yearFormat (date) {
      return moment(date).format('L')
    }
  },
  watch: {
    schoolId () {
      this.$store.dispatch('getUsersFromSchoolId', this.schoolId)
      this.getTags()
    },
    activeSchoolYear () {
      this.setEndDate(this.activeSchoolYear.employee_time_end)
      this.setStartDate(this.activeSchoolYear.employee_time_start)
    },
    userSettings (val) {
      if (val && val.assign_hours_view_setting) {
        this.updateViewSettingsFromUserSettings()
      }
    },
    schoolPrepFactor () {
      if (this.isChangePrepFactorModal) {
        this.setIsChangePrepFactorModal(false)
        this.isSavingPrepFactor = false
      }
    },
    isPrepFactorForSubstitutePools () {
      if (this.isChangePrepFactorModal) {
        this.setIsChangePrepFactorModal(false)
        this.isSavingPrepFactor = false
      }
    }
  },
  computed: {
    ...mapGetters([
      'usersSortedOnName',
      'teachingHoursPerUser',
      'tasksHoursInUserHash',
      'preparationHoursInUserHash',
      'localPoolHoursInUserHash',
      'clubHoursInUserHash',
      'schoolId',
      'weightedPreparationHoursForCalendarId',
      'explicitPrepTaskHoursForCalendarId',
      'isAssignedPoolFromNormWhenPoolsDeactivated',
      'isSchoolSystem'
    ]),
    isLoadingSubjects () {
      return this.$store.state.subjects.isLoadingSubjects
    },
    isLoadingTasks () {
      return this.$store.state.subjects.isLoadingTasks
    },
    isLoadingSubjectsOrTasks () {
      return this.isLoadingSubjects || this.isLoadingTasks
    },
    usersWithEmployeeId () {
      return this.usersSortedOnName.filter(user => user.employee_id)
    },
    viewSettingsI18N () {
      let i18nSettings = {}
      Object.keys(this.viewSettings).forEach(key => {
        let setting = {...this.viewSettings[key]}
        setting.title = this.i18n[key + '_title'] || setting.title
        i18nSettings[key] = setting
      })
      return i18nSettings
    },
    theUser () {
      return this.$store.state.user
    },
    isLocalPoolActivated () {
      return this.$store.getters.isSchoolFeatureLocalePool
    },
    activeSchool () {
      return this.$store.state.activeSchool ? this.$store.state.activeSchool : window.SCHOOL ? window.SCHOOL : null
    },
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    localPoolTitle () {
      const title = this.$store.getters.localPoolTitle
      return title || this.$t('vue2.task_overview.pool')
    },
    isSchoolDanish () {
      return this.activeSchool.country.toLowerCase() === 'dk'
    },
    isSchoolFromCO () {
      return this.locale.toLowerCase() === 'en'
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    theUserCanManageAssignedHours () {
      return this.userHasPermission('can_manage_school')
    },
    isDatesPristine () {
      return this.compareDates(this.activeSchoolYear.employee_time_start, this.startDate) && this.compareDates(this.activeSchoolYear.employee_time_end, this.endDate)
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    selectedSchoolYear () {
      return this.activeSchoolYear.name
    },
    assignHoursTagFiltersStorageKey () {
      if (this.schoolId == null) {
        return null
      }
      return 'docendo.assignHours.tagFilters.' + String(this.schoolId)
    },
    userTagsToShowSum () {
      return this.$store.getters.userTagsWithoutPrepFactor
    },
    userTagsForSumWorkHours () {
      return this.$store.getters.userTagsForSumWorkHours
    },
    usersForList () {
      const getSafe = (obj, key) => obj && obj[key] ? obj[key] : 0
      const toNum = val => {
        if (val === null || val === undefined || val === '') {
          return 0
        }
        const n = parseFloat(val)
        return isNaN(n) ? 0 : n
      }
      const toMinutes = val => this.viewSettings.show_in_minutes.active ? val * 60 : val

      // Read store getters once: each read of a getter copies all of its (many) reactive deps
      const isPoolsDeactivated = this.isPoolsDeactivated
      const isAssignedPoolFromNorm = this.isAssignedPoolFromNormWhenPoolsDeactivated
      const userTagsToShowSum = this.userTagsToShowSum
      const userTagsForSumWorkHours = this.userTagsForSumWorkHours
      const hasEmployeeIds = this.usersWithEmployeeId.length > 0
      const usersWithAssignedHours = this.usersWithAssignedHours
      const usersWithScheduledHours = this.usersWithScheduledHours
      const hoursHashes = isPoolsDeactivated ? {
        teaching: this.teachingHoursPerUser,
        tasks: this.tasksHoursInUserHash,
        club: this.clubHoursInUserHash,
        localPool: this.localPoolHoursInUserHash,
        preparation: this.preparationHoursInUserHash,
        weightedPreparationFor: this.weightedPreparationHoursForCalendarId,
        explicitPreparationFor: this.explicitPrepTaskHoursForCalendarId
      } : null

      return this.usersSortedOnName.map(obj => {
        const id = obj.id
        const calId = obj.user_calendar_id
        const isUserPedagog = userTagsToShowSum.length === 0 ? false : userTagsToShowSum.some(tag => obj.user_tags.includes(tag))
        const isUserWithSumWorkHours = userTagsForSumWorkHours.length === 0 ? false : userTagsForSumWorkHours.some(tag => obj.user_tags.includes(tag))

        const assigned = usersWithAssignedHours[id] || {}
        const scheduled = usersWithScheduledHours[id] || {}

        let assignedTotalHours = toNum(getSafe(assigned, 'assigned_total_hours'))
        let substitutePoolHours = toNum(getSafe(assigned, 'substitute_pool_hours'))
        let totalHoursAssignedInPeriod = assignedTotalHours ? toNum(this.getAssignedHoursForPeriod(assigned.assigned_total_hours)) : 0

        let assignedTeachingHours = 0
        let assignedAdditionalTaskHours = 0
        let assignedPreparationHours = 0
        let assignedClubHours = 0
        let assignedPoolHours = 0
        let sumHours = 0
        let restHours = 0

        if (!isPoolsDeactivated) {
          assignedTeachingHours = toNum(getSafe(assigned, 'assigned_teaching_hours'))
          assignedAdditionalTaskHours = toNum(getSafe(assigned, 'assigned_additional_task_hours'))
          assignedPreparationHours = toNum(getSafe(assigned, 'assigned_preparation_hours'))
          assignedClubHours = toNum(getSafe(assigned, 'assigned_club_hours'))
          assignedPoolHours = toNum(getSafe(assigned, 'assigned_pool_hours'))
        } else {
          assignedTeachingHours = toNum(getSafe(hoursHashes.teaching, calId))
          assignedAdditionalTaskHours = toNum(getSafe(hoursHashes.tasks, calId))
          assignedPreparationHours = toNum(this.getPreparationForUser(assignedTeachingHours, calId, obj, substitutePoolHours, hoursHashes))
          assignedClubHours = toNum(getSafe(hoursHashes.club, calId))
          assignedPoolHours = isAssignedPoolFromNorm
            ? toNum(getSafe(assigned, 'assigned_pool_hours'))
            : toNum(getSafe(hoursHashes.localPool, calId))
        }
        sumHours = assignedTeachingHours + assignedAdditionalTaskHours + assignedPreparationHours + assignedClubHours + assignedPoolHours + substitutePoolHours
        restHours = assignedTotalHours - sumHours

        let actualTeachingHours = getSafe(scheduled, 'actual_teaching_hours')
        let actualSubstituteHours = getSafe(scheduled, 'actual_substitute_hours')
        let actualAbsenceHours = getSafe(scheduled, 'actual_absence_hours')
        let actualAdditionalTaskHours = getSafe(scheduled, 'actual_additional_task_hours')
        let actualPreparationHours = getSafe(scheduled, 'actual_preparation_hours')
        let actualClubHours = getSafe(scheduled, 'actual_club_hours')
        let actualPoolHours = getSafe(scheduled, 'actual_pool_hours')

        let actualTotalHours = isUserWithSumWorkHours
          ? actualSubstituteHours + actualTeachingHours + actualAdditionalTaskHours + actualPreparationHours + actualClubHours + actualPoolHours
          : getSafe(scheduled, 'actual_total_hours')

        // Konverter til minutter hvis nødvendigt
        actualTeachingHours = toMinutes(actualTeachingHours)
        actualSubstituteHours = toMinutes(actualSubstituteHours)
        actualAbsenceHours = toMinutes(actualAbsenceHours)
        actualAdditionalTaskHours = toMinutes(actualAdditionalTaskHours)
        actualPreparationHours = toMinutes(actualPreparationHours)
        actualClubHours = toMinutes(actualClubHours)
        actualPoolHours = toMinutes(actualPoolHours)
        actualTotalHours = toMinutes(actualTotalHours)

        const user = {
          id: id,
          user_calendar_id: calId,
          v_rest_hours: Math.round(restHours * 100) / 100,
          img: obj.default_thumb_url,
          name: obj.name,
          tags: obj.user_tags,
          v_total_hours_assigned_in_period: this.humanizeNumber(totalHoursAssignedInPeriod),
          assigned_total_hours: this.humanizeNumber(assignedTotalHours),
          actual_total_hours: this.humanizeNumber(actualTotalHours),
          diff_total_hours: this.humanizeNumber(totalHoursAssignedInPeriod - actualTotalHours),
          assigned_teaching_hours: this.humanizeNumber(assignedTeachingHours),
          actual_teaching_hours: this.humanizeNumber(actualTeachingHours),
          diff_teaching_hours: this.humanizeNumber(assignedTeachingHours - actualTeachingHours),
          substitute_pool_hours: this.humanizeNumber(substitutePoolHours),
          actual_substitute_hours: this.humanizeNumber(actualSubstituteHours),
          diff_substitute_hours: this.humanizeNumber(substitutePoolHours - actualSubstituteHours),
          actual_absence_hours: this.humanizeNumber(actualAbsenceHours),
          assigned_additional_task_hours: this.humanizeNumber(assignedAdditionalTaskHours),
          actual_additional_task_hours: this.humanizeNumber(actualAdditionalTaskHours),
          diff_additional_task_hours: this.humanizeNumber(assignedAdditionalTaskHours - actualAdditionalTaskHours),
          assigned_preparation_hours: this.humanizeNumber(assignedPreparationHours),
          actual_preparation_hours: this.humanizeNumber(actualPreparationHours),
          diff_preparation_hours: this.humanizeNumber(assignedPreparationHours - actualPreparationHours),
          assigned_club_hours: this.humanizeNumber(assignedClubHours),
          actual_club_hours: this.humanizeNumber(actualClubHours),
          diff_club_hours: this.humanizeNumber(assignedClubHours - actualClubHours),
          assigned_pool_hours: this.humanizeNumber(assignedPoolHours),
          actual_pool_hours: this.humanizeNumber(actualPoolHours),
          diff_pool_hours: this.humanizeNumber(assignedPoolHours - actualPoolHours),
          rest_hours: this.humanizeNumber(restHours),
          sum_hours: this.humanizeNumber(sumHours)
        }

        if (hasEmployeeIds) {
          user.employee_id = obj.employee_id || null
        }
        if (isUserWithSumWorkHours) {
          user['vIsPedagogog'] = true
        }
        if (isUserPedagog) {
          user['vNoPrepFactor'] = true
        }
        return user
      })
    },
    usersWithPositiveRest () {
      return this.usersForList
        .filter(user => user.user_calendar_id && user.v_rest_hours > 0)
        .map(user => ({
          id: user.id,
          name: user.name,
          user_calendar_id: user.user_calendar_id,
          assigned_total_hours: user.assigned_total_hours,
          rest: user.v_rest_hours
        }))
    },
    searchedUsers () {
      return this.usersForList.filter(user => user.name.match(new RegExp(this.searchText, 'i')))
    },
    filteredUsers () {
      if (this.appliedFilters.length === 0) {
        return this.searchedUsers
      }
      return this.searchedUsers.filter(user => {
        return this.appliedFilters.some(filter => user.tags.indexOf(filter) > -1)
      })
    },
    isLoadingUsers () {
      return this.$store.state.isLoadingAllUsers
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    dataToExcel () {
      const users = this.filteredUsers.map(user => {
        /*eslint-disable */
        let userObject = {
          'name': user.name,
          'assigned_1': parseFloat(this.humanizeNumber(user.assigned_total_hours)),
          'scheduled_1': parseFloat(user.actual_total_hours),
          'diff_1': parseFloat(user.diff_total_hours),
          'assigned_2': parseFloat(user.assigned_teaching_hours),
          'scheduled_2': parseFloat(user.actual_teaching_hours),
          'diff_2': parseFloat(user.diff_teaching_hours),
          'assigned_3': parseFloat(user.assigned_additional_task_hours),
          'scheduled_3': parseFloat(user.actual_additional_task_hours),
          'diff_3': parseFloat(user.diff_additional_task_hours),
          'assigned_4': parseFloat(user.assigned_preparation_hours),
          'scheduled_4': parseFloat(user.actual_preparation_hours),
          'diff_4': parseFloat(user.diff_preparation_hours),
          'assigned_8': parseFloat(user.assigned_pool_hours),
          'scheduled_8': parseFloat(user.actual_pool_hours),
          'diff_8': parseFloat(user.diff_pool_hours),
          'scheduled_5': parseFloat(user.actual_absence_hours),
          'assigned_6': parseFloat(user.substitute_pool_hours),
          'scheduled_6': parseFloat(user.actual_substitute_hours),
          'diff_6': parseFloat(user.diff_substitute_hours),
          'assigned_7': parseFloat(user.assigned_club_hours),
          'scheduled_7': parseFloat(user.actual_club_hours),
          'diff_7': parseFloat(user.diff_club_hours)
        }
        // Conditionally add 'employee_id' if the condition is met
        if (this.usersWithEmployeeId.length > 0) {
          userObject['employee_id'] = user['employee_id'] ? user['employee_id'] : null
        }
        return userObject
        /*eslint-enable */
      })
      let baseHeaders = {
        'name': '',
        'assigned_1': '',
        'scheduled_1': '',
        'diff_1': '',
        'assigned_2': '',
        'scheduled_2': '',
        'diff_2': '',
        'assigned_3': '',
        'scheduled_3': '',
        'diff_3': '',
        'assigned_4': '',
        'scheduled_4': '',
        'diff_4': '',
        'assigned_8': '',
        'scheduled_8': '',
        'diff_8': '',
        'scheduled_5': '',
        'assigned_6': '',
        'scheduled_6': '',
        'diff_6': '',
        'assigned_7': '',
        'scheduled_7': '',
        'diff_7': ''
      }

      // Conditionally include 'employee_id' as the first column if applicable
      if (this.usersWithEmployeeId.length > 0) {
        baseHeaders = {'employee_id': 'Employee ID', ...baseHeaders}
      }

      // Prepare the first header row, primarily for Excel formatting (merge cells, etc.)
      const headersRow1 = Object.keys(baseHeaders).reduce((accumulator, currentKey) => {
        accumulator[currentKey] = '' // Fill the first header row with empty strings
        return accumulator
      }, {})

      const headersRow2 = {...baseHeaders}
      users.unshift(headersRow2, headersRow1)
      return users
    },
    workDayDates () {
      const self = this

      function isNotWeekend (m) {
        return moment(m).isoWeekday() !== 6 && moment(m).isoWeekday() !== 7
      }

      function isNotVacationDay (m) {
        const idx = self.allVacationsDays.findIndex(date => moment(date.day).format('YYYY-MM-DD') === m)
        return idx < 0
      }

      let startDate = moment(this.activeSchoolYear.employee_time_start).format('YYYY-MM-DD')
      const endDate = moment(this.activeSchoolYear.employee_time_end).format('YYYY-MM-DD')
      const dateArray = []
      while (moment(startDate).isSameOrBefore(endDate)) {
        const m = moment(startDate).format('YYYY-MM-DD')
        if (isNotWeekend(m) && isNotVacationDay(m)) {
          dateArray.push(m)
        }
        startDate = moment(startDate).add(1, 'days')
      }
      return dateArray
    },
    workDayDatesInPeriod () {
      let startDate = moment(this.startDate).format('YYYY-MM-DD')
      const endDate = moment(this.endDate).format('YYYY-MM-DD')
      const dateArray = []
      while (moment(startDate).isSameOrBefore(endDate)) {
        const m = moment(startDate).format('YYYY-MM-DD')
        dateArray.push(m)
        startDate = moment(startDate).add(1, 'days')
      }
      return dateArray.filter(date => {
        const m = moment(date).format('YYYY-MM-DD')
        const idx = this.workDayDates.indexOf(m)
        return idx > -1
      })
    },
    numberOfWorkDays () {
      return this.workDayDates.length
    },
    numberOfWorkDaysInPeriod () {
      return this.workDayDatesInPeriod.length
    },
    userSettings () {
      return this.$store.state.userSettings
    },
    schoolSettings () {
      return this.$store.state.schoolSettings
    },
    schoolPrepFactor () {
      return this.schoolSettings && this.schoolSettings['prepFactor'] ? this.schoolSettings['prepFactor'] : null
    },
    isPrepFactorForSubstitutePools () {
      return !!(this.schoolSettings && this.schoolSettings['prepFactorForSubstitutePools'])
    },
    schoolThresholdToHideTaskHours () {
      return this.schoolSettings && this.schoolSettings['thresholdToHideTaskHours'] ? this.schoolSettings['thresholdToHideTaskHours'] : null
    },
    isPoolsDeactivated () {
      return this.schoolSettings && this.schoolSettings['deactivatePools'] ? this.schoolSettings['deactivatePools'] : false
    },
    isShowRestColumn () {
      return this.schoolSettings && this.schoolSettings['showRestColumn'] ? this.schoolSettings['showRestColumn'] : false
    }
  },
  methods: {
    setIsModalPedagogicalSetting (bool) {
      this.isModalPedagogicalSetting = bool
    },
    getIsUserInGroupWithoutPrepFactor (user) {
      const groupsWithoutPrepFactor = this.userTagsToShowSum
      return user.user_tags.some(tag => groupsWithoutPrepFactor.includes(tag))
    },
    getPreparationForUser (assignedTeachingHours, calId, userObj, substitutePoolHours, hoursHashes) {
      const getSafe = (obj, key) => obj && obj[key] ? obj[key] : 0
      if (this.getIsUserInGroupWithoutPrepFactor(userObj)) {
        return hoursHashes.explicitPreparationFor(calId)
      }
      const prepFactorNum = parseNumericPreparationFactor(this.schoolPrepFactor)
      // Vikarpuljen (substitute_pool_hours) er et budget-tal på user_year og indgår ikke i
      // den vægtede brik-beregning — lægges derfor til her, når indstillingen er aktiv.
      const substitutePrepHours = this.isPrepFactorForSubstitutePools && prepFactorNum !== null
        ? (substitutePoolHours || 0) * prepFactorNum
        : 0
      const weighted = hoursHashes.weightedPreparationFor(calId)
      if (weighted !== null && weighted !== undefined) {
        return weighted + substitutePrepHours
      }
      if (prepFactorNum !== null) {
        return assignedTeachingHours * prepFactorNum + substitutePrepHours
      }
      return getSafe(hoursHashes.preparation, calId)
    },
    setIsShowEmploymentEnded (bool) {
      this.isShowEmploymentEnded = bool
    },
    setIsThePrepFactorActive () {
      this.isThePrepFactorActive = !this.isThePrepFactorActive
      if (this.isThePrepFactorActive) {
        this.$nextTick(_ => {
          this.$refs.the_prep_factor_input.focus()
        })
      }
    },
    savePrepFactor () {
      this.isSavingPrepFactor = true
      if (!this.isThePrepFactorActive) {
        // null fjerner nøglerne atomart i samme save
        this.$store.dispatch('updateSchoolSettings', {'prepFactor': null, 'prepFactorForSubstitutePools': null})
      } else {
        const val = this.thePrepFactor * 1
        if (isNaN(Number(val))) {
          eventbus.toast = {text: 'Kan ikke gemme. Skal være et tal', 'success': false}
          this.isSavingPrepFactor = false
          return
        }
        this.$store.dispatch('updateSchoolSettings', {
          'prepFactor': val,
          'prepFactorForSubstitutePools': this.isThePrepFactorForSubstitutePools ? true : null
        })
      }
    },
    setIsChangePrepFactorModal (bool) {
      this.isChangePrepFactorModal = bool
      if (bool && this.schoolPrepFactor) {
        this.thePrepFactor = this.schoolPrepFactor
        this.isThePrepFactorActive = true
      }
      if (bool) {
        this.isThePrepFactorForSubstitutePools = this.isPrepFactorForSubstitutePools
      }
    },
    setIsDeactivatePoolsModalVisible (bool) {
      this.isDeactivatePoolsModalVisible = bool
    },
    reReadNumbers () {
      this.isDeactivatePoolsModalVisible = false
      this.getUsersHours()
      this.readSubjectsAndTasks()
    },
    updateViewSettingsFromUserSettings () {
      const val = this.userSettings // Assuming userSettings is reactive data
      if (val && val.assign_hours_view_setting) {
        const settingsChanged = Object.entries(val.assign_hours_view_setting).some(([key, setting]) => {
          if (!this.viewSettings[key]) return true // Key does not exist in viewSettings
          return this.viewSettings[key].active !== setting.active || this.viewSettings[key].title !== setting.title
        }) || Object.keys(this.viewSettings).length !== Object.keys(val.assign_hours_view_setting).length // Check if number of keys differ

        if (settingsChanged) {
          this.viewSettings = JSON.parse(JSON.stringify(val.assign_hours_view_setting))
        }
      }
    },
    setViewSetting (key) {
      // Toggle the active state of the setting
      if (this.viewSettings[key]) {
        this.viewSettings[key].active = !this.viewSettings[key].active
      }

      // Save the updated settings to user settings
      this.$store.dispatch('updateUserSettings', {'assign_hours_view_setting': this.viewSettings})
    },
    setIsChangeViewModal () {
      if (this.isChangeViewModal) {
        this.isChangeViewModal = false
      } else {
        this.isChangeViewModal = true
      }
    },
    asyncLoadXLSXAndDownload () {
      this.isDownloading = true
      if (window.XLSX && XLSX) {
        this.download()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        let intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.download()
          }
        }, 500)
      }
    },
    download () {
      // https://stackoverflow.com/questions/61723105/achieving-nested-header-structure-while-writing-excel-using-sheetjs-exceljs
      let data = XLSX.utils.json_to_sheet(this.dataToExcel, {skipHeader: true})
      if (this.usersWithEmployeeId.length === 0) {
        data.A1 = {t: 's', v: this.i18n.name}
        data.B1 = {t: 's', v: this.i18n.workTime}
        data.B2 = {t: 's', v: this.i18n.assigned}
        data.C2 = {t: 's', v: this.i18n.scheduled}
        data.D2 = {t: 's', v: this.i18n.difference}
        data.E1 = {t: 's', v: this.i18n.teaching}
        data.E2 = {t: 's', v: this.i18n.assigned}
        data.F2 = {t: 's', v: this.i18n.scheduled}
        data.G2 = {t: 's', v: this.i18n.difference}
        data.H1 = {t: 's', v: this.i18n.otherTasks}
        data.H2 = {t: 's', v: this.i18n.assigned}
        data.I2 = {t: 's', v: this.i18n.scheduled}
        data.J2 = {t: 's', v: this.i18n.difference}
        data.K1 = {t: 's', v: this.i18n.preparation}
        data.K2 = {t: 's', v: this.i18n.assigned}
        data.L2 = {t: 's', v: this.i18n.scheduled}
        data.M2 = {t: 's', v: this.i18n.difference}
        data.N1 = {t: 's', v: this.localPoolTitle}
        data.N2 = {t: 's', v: this.i18n.assigned}
        data.O2 = {t: 's', v: this.i18n.scheduled}
        data.P2 = {t: 's', v: this.i18n.difference}
        data.Q1 = {t: 's', v: this.i18n.absence}
        data.Q2 = {t: 's', v: this.i18n.scheduled}
        data.R1 = {t: 's', v: this.i18n.substitute}
        data.R2 = {t: 's', v: this.i18n.assigned}
        data.S2 = {t: 's', v: this.i18n.scheduled}
        data.T2 = {t: 's', v: this.i18n.difference}
        data.U1 = {t: 's', v: this.i18n.sfo}
        data.U2 = {t: 's', v: this.i18n.assigned}
        data.V2 = {t: 's', v: this.i18n.scheduled}
        data.W2 = {t: 's', v: this.i18n.difference}
        // s - start, e - end, r - row, c - col (0 based)
        const merge = [
          {s: {r: 0, c: 0}, e: {r: 1, c: 0}},  // Name
          {s: {r: 0, c: 1}, e: {r: 0, c: 3}},  // WorkTime
          {s: {r: 0, c: 4}, e: {r: 0, c: 6}},  // Teaching
          {s: {r: 0, c: 7}, e: {r: 0, c: 9}},  // Other Tasks
          {s: {r: 0, c: 10}, e: {r: 0, c: 12}}, // Preparation
          {s: {r: 0, c: 13}, e: {r: 0, c: 15}}, // Pool
          {s: {r: 0, c: 16}, e: {r: 1, c: 16}}, // Absence (kun én kolonne)
          {s: {r: 0, c: 17}, e: {r: 0, c: 19}}, // Substitute
          {s: {r: 0, c: 20}, e: {r: 0, c: 22}} // SFO
        ] // Measurements
        data['!merges'] = merge
      } else {
        data.A1 = {t: 's', v: this.i18n.employee_id}
        data.B1 = {t: 's', v: this.i18n.name}
        data.C1 = {t: 's', v: this.i18n.workTime}
        data.C2 = {t: 's', v: this.i18n.assigned}
        data.D2 = {t: 's', v: this.i18n.scheduled}
        data.E2 = {t: 's', v: this.i18n.difference}
        data.F1 = {t: 's', v: this.i18n.teaching}
        data.F2 = {t: 's', v: this.i18n.assigned}
        data.G2 = {t: 's', v: this.i18n.scheduled}
        data.H2 = {t: 's', v: this.i18n.difference}
        data.I1 = {t: 's', v: this.i18n.otherTasks}
        data.I2 = {t: 's', v: this.i18n.assigned}
        data.J2 = {t: 's', v: this.i18n.scheduled}
        data.K2 = {t: 's', v: this.i18n.difference}
        data.L1 = {t: 's', v: this.i18n.preparation}
        data.L2 = {t: 's', v: this.i18n.assigned}
        data.M2 = {t: 's', v: this.i18n.scheduled}
        data.N2 = {t: 's', v: this.i18n.difference}
        data.O1 = {t: 's', v: this.i18n.absence}
        data.O2 = {t: 's', v: this.i18n.scheduled}
        data.P1 = {t: 's', v: this.i18n.substitute}
        data.P2 = {t: 's', v: this.i18n.assigned}
        data.Q2 = {t: 's', v: this.i18n.scheduled}
        data.R2 = {t: 's', v: this.i18n.difference}
        data.S1 = {t: 's', v: this.i18n.sfo}
        data.S2 = {t: 's', v: this.i18n.assigned}
        data.T2 = {t: 's', v: this.i18n.scheduled}
        data.U2 = {t: 's', v: this.i18n.difference}
        // s - start, e - end, r - row, c - col (0 based)
        const merge = [
          {s: {r: 0, c: 0}, e: {r: 1, c: 0}}, // Employee ID
          {s: {r: 0, c: 1}, e: {r: 1, c: 1}}, // Name
          {s: {r: 0, c: 2}, e: {r: 0, c: 4}}, // Work Time, spans columns 2 to 4
          {s: {r: 0, c: 5}, e: {r: 0, c: 7}}, // Teaching, spans columns 5 to 7
          {s: {r: 0, c: 8}, e: {r: 0, c: 10}}, // Other Tasks, spans columns 8 to 10
          {s: {r: 0, c: 11}, e: {r: 0, c: 13}}, // Preparation, spans columns 11 to 13
          {s: {r: 0, c: 14}, e: {r: 0, c: 14}}, // Absence, only Scheduled, so it's just column 14
          {s: {r: 0, c: 15}, e: {r: 0, c: 17}}, // Substitute, spans columns 15 to 17
          {s: {r: 0, c: 18}, e: {r: 0, c: 20}} // SFO, spans columns 18 to 20
          // Add additional merges as necessary based on your data structure
        ]
        data['!merges'] = merge
      }
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, data, 'data')
      XLSX.writeFile(wb, 'docendo_tildel_timer.xlsx')
      this.isDownloading = false
    },
    getAssignedHoursForPeriod (hours) {
      if (this.isDatesPristine) {
        return hours
      } else {
        const hoursPerDay = hours / this.numberOfWorkDays
        return hoursPerDay * this.numberOfWorkDaysInPeriod
      }
    },
    humanizeNumber (num) {
      return parseFloat(num).toFixed(1)
    },
    debounceSearch (event) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.searchText = event.target.value
      }, 400)
    },
    resetDates () {
      this.setEndDate(this.activeSchoolYear.employee_time_end, false)
      this.setStartDate(this.activeSchoolYear.employee_time_start)
    },
    setDatesFromRange (dateRange) {
      this.setDates(dateRange.start, dateRange.end)
    },
    setDates (start, end) {
      if (start) {
        this.startDate = start
      }
      if (end) {
        this.endDate = end
      }
      this.getUsersActualHours()
    },
    setStartDate (date) {
      const isUnchanged = this.startDate && this.compareDates(this.startDate, date)
      this.startDate = date
      if (!isUnchanged && moment(date).isValid()) {
        this.getUsersHours()
      }
    },
    setEndDate (date, isCallServer) {
      const isUnchanged = this.endDate && this.compareDates(this.endDate, date)
      this.endDate = date
      if (!isCallServer || isUnchanged) {
        return
      }
      if (moment(date).isValid()) {
        this.getUsersHours()
      }
    },
    setTheUserToShowTasks (user) {
      if (this.allUsersInHash[user.id]) {
        this.theUserToShowTasks = this.allUsersInHash[user.id]
      }
    },
    closeTheUserToShowTasks () {
      this.theUserToShowTasks = null
    },
    setActiveUser (userAndFilter) {
      this.activeUserId = userAndFilter.user.id
      this.activeUserEventFilter = userAndFilter.filter
    },
    updateUserHours () {
      eventbus.toast = {text: this.i18n['updating_numbesr_you_will_be_notified'], 'success': false}
      if (this.isUpdateBttnLoading) {
        return
      }
      this.isUpdateBttnLoading = true
      docendo.schoolRecalculateAll(this.schoolId, data => {
        this.getUsersHours()
        this.isUpdateBttnLoading = false
      }, error => {
        console.log('error', error)
      })
    },
    toggleTagFilter (tag) {
      const idx = this.appliedFilters.indexOf(tag)
      if (idx > -1) {
        this.appliedFilters.splice(idx, 1)
      } else {
        this.appliedFilters.push(tag)
      }
      this.persistAppliedTagFilters()
    },
    clearTagFilters () {
      this.appliedFilters = []
      this.persistAppliedTagFilters()
    },
    persistAppliedTagFilters () {
      const key = this.assignHoursTagFiltersStorageKey
      if (!key) {
        return
      }
      this.$helpers.setLocalStorage(key, JSON.stringify(this.appliedFilters))
    },
    restoreAppliedTagFiltersFromStorage () {
      const key = this.assignHoursTagFiltersStorageKey
      if (!key) {
        return
      }
      const raw = this.$helpers.getLocalStorage(key)
      if (!raw) {
        return
      }
      let parsed = null
      try {
        parsed = JSON.parse(raw)
      } catch (e) {
        return
      }
      if (!Array.isArray(parsed)) {
        return
      }
      this.appliedFilters = parsed.filter(tag => this.tags.indexOf(tag) > -1)
    },
    toggleShowRestColumn () {
      if (this.isShowRestColumn) {
        this.$store.dispatch('deleteSchoolSetting', 'showRestColumn')
      } else {
        this.$store.dispatch('updateSchoolSettings', {'showRestColumn': true})
      }
    },
    removeAllSelectedUsers () {
      this.selectedUsers = []
      this.createTagActive = false
    },
    setUser (user, idx) {
      if (this.selectedUsers.indexOf(user.id) > -1) {
        this.selectedUsers.splice(this.selectedUsers.indexOf(user.id), 1)
      } else {
        this.selectedUsers.push(user.id)
      }
    },
    setAllUsersSelected () {
      this.selectedUsers = []
      for (let i = 0; i < this.filteredUsers.length; i++) {
        this.selectedUsers.push(this.filteredUsers[i].id)
      }
    },
    updatedUser (user) {
      let id = (user.user_id).toString()
      this.$set(this.usersWithAssignedHours, id, user)
    },
    getTags () {
      docendo.schoolTags(this.schoolId, data => {
        this.tags = data
        this.restoreAppliedTagFiltersFromStorage()
      })
    },
    getUsersActualHours () {
      this.isLoadingUpdatedNumbers = true
      const startDate = moment(this.startDate).format('YYYY-MM-DD') + ' 00:00'
      const endDate = moment(this.endDate).format('YYYY-MM-DD') + ' 23:59'
      docendo.schoolActualHours(this.schoolId, startDate, endDate, null, data => {
        let newUsers = {}
        for (let i = 0; i < data.length; i++) {
          newUsers[data[i].user_id] = data[i]
        }
        this.usersWithScheduledHours = newUsers
        this.isLoadingUpdatedNumbers = false
      })
    },
    getUsersHours () {
      this.getUsersActualHours()
      docendo.schoolAssignedHours(this.schoolId, this.selectedSchoolYear, null, data => {
        let newUsers = {}
        for (let i = 0; i < data.length; i++) {
          newUsers[data[i].user_id] = data[i]
        }
        this.usersWithAssignedHours = newUsers
        this.isInitalLoad = false
      })
      this.readSubjectsAndTasks()
    },
    readSubjectsAndTasks () {
      if (this.isPoolsDeactivated) {
        this.$store.dispatch('readSubjects', this.activeSchoolYear.id)
      }
    },
    compareDates (firstDate, secondDate) {
      return moment(firstDate).format('YYYY-MM-DD') === moment(secondDate).format('YYYY-MM-DD')
    },
    readVacationDays () {
      docendo.schoolVacationDaysRead(window.SCHOOL.id, success => {
        if (success.status !== 'error') {
          this.allVacationsDays = success
        }
      }, failure => {
        console.log('failure')
      })
    },
    userHasPermission (permission) {
      if (!this.theUser) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.theUser.admin
      } else {
        return true
      }
    }
  },
  beforeMount () {
    this.$store.dispatch('getUsersFromSchoolId', this.activeSchool.id)
    this.readVacationDays()
    this.getTags()
  },
  mounted () {
    if (this.activeSchoolYear) {
      this.setEndDate(this.activeSchoolYear.employee_time_end, false)
      this.setStartDate(this.activeSchoolYear.employee_time_start)
    }
    this.updateViewSettingsFromUserSettings()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.do-modal--fullscreen {
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
}

.do-modal--fullscreen .do-modal__body {
  flex: 1;
  max-height: none;
  padding: 1rem 2rem;
}

.users-hours__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.users-hours__header__left {
  align-items: center;
  display: flex;
}

.users-hours__header__left > select {
  margin-left: 20px;
}

.users {
  position: relative;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #fff;
}

.users--loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding-top: 100px;
  bottom: 0;
  background: rgb(255 255 255 / 40%);
  z-index: 123123;
}

.users-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 50px;
  font-size: 14px;
  font-weight: bold;
  border-bottom: 1px solid #d8d8d8;
}

.users-header-second {
  display: flex;
  justify-content: flex-start;
  font-size: 12px;
  padding: 0;
}

.users-header-second__headline {
  display: flex;
  align-items: center;
}

.users-header__searchfield {
  width: 250px;
}

.users-user__meta {
  display: flex;
  align-items: center;
  flex: 1;
  padding-left: 1rem;
}

.flex-left {
  display: flex;
  flex: 1;
}

.flex-right {
  padding-right: 1rem;
}

.user-task__container {
  height: calc(100vh - 2rem);
  width: calc(100vw - 2rem);
  max-width: 1440px;
  padding: 2rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.user-task__container__header {
  margin: -1rem -2rem 1rem -2rem;
  border-radius: 4px 4px 0 0;
  display: flex;
  padding: 0 2rem;
  height: 2rem;
  justify-content: space-between;
  align-items: center;
}

.user-tasks__header__right {
  display: flex;
  align-items: center;
}

.user-tasks__header__right .d-link {
  color: var(--blue);
  cursor: pointer;
  margin-right: 1rem;
}

.user-tasks__header__right .d-link:hover {
  text-decoration: underline;
}

.marg-05-right {
  margin-right: 0.5rem;
}

.users-header-second__buttons {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.modal-assign-hours-view {
  min-width: 400px;
}

.modal-assign-hours-view__item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  margin: 0.25rem 0.5rem;
}

.modal-assign-hours-view__item:hover {
  background-color: #eee;
}

.modal-assign-hours-view__item label {
  color: var(--dark-blue);
}

.do-modal__body {
  background: #edf0f5;
  max-height: calc(100vh - 105px);
  overflow-y: auto;
  overscroll-behavior: contain;
  min-width: 500px;
  padding: 1rem;
}

.d-label-and-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 2rem;
}

.d-label-and-toggle label {
  cursor: pointer;
}

.d-label-and-toggle:hover {
  background: aliceblue;
}

.prep-factor-input {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.prep-factor-input > input {
  width: 80px;
  text-align: center;
  background: #fff;
}

.color-dark {
  color: #222 !important;
}

.d-explainer-box {
  border: 1px solid #ccc;
  padding: 1rem;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  width: 480px;
}

.d-explainer-box .tw-icon {
  min-width: 24px;
}

</style>



// WEBPACK FOOTER //
// AssignHours.vue?5f999f93